import type { QuickLauncherAction } from "./quick-launcher.types";

/**
 * Match strength, best first. Deliberately a small ordered ladder rather than a
 * fuzzy score: with a few dozen entries, "the thing I typed the start of" is
 * always what the user meant, and a fuzzy matcher mostly buys surprising
 * reorderings.
 */
// A plain object rather than an enum: ts-jest runs with `isolatedModules`, where
// `const enum` cannot be transpiled file-by-file.
const MatchRank = {
  LabelPrefix: 0,
  LabelWordPrefix: 1,
  LabelSubstring: 2,
  KeywordMatch: 3,
  DescriptionMatch: 4,
  EveryWordPrefix: 5,
  NoMatch: 6
} as const;

type MatchRankValue = (typeof MatchRank)[keyof typeof MatchRank];

const toWords = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * "line items report" and "report line items" both name Reports › Sales › Line
 * Items, in either order, without any one field containing the whole phrase.
 */
const everyTokenPrefixesAWord = (
  action: QuickLauncherAction,
  query: string
): boolean => {
  const tokens = toWords(query);
  if (tokens.length < 2) return false;
  const words = [
    action.label,
    action.description ?? "",
    ...(action.keywords ?? [])
  ].flatMap(toWords);
  return tokens.every((token) => words.some((word) => word.startsWith(token)));
};

const rankAction = (
  action: QuickLauncherAction,
  query: string
): MatchRankValue => {
  const label = action.label.toLowerCase();

  if (label.startsWith(query)) return MatchRank.LabelPrefix;
  if (label.split(/\s+/).some((word) => word.startsWith(query))) {
    return MatchRank.LabelWordPrefix;
  }
  if (label.includes(query)) return MatchRank.LabelSubstring;
  if (
    action.keywords?.some((keyword) => keyword.toLowerCase().includes(query))
  ) {
    return MatchRank.KeywordMatch;
  }
  if (action.description?.toLowerCase().includes(query)) {
    return MatchRank.DescriptionMatch;
  }
  if (everyTokenPrefixesAWord(action, query)) return MatchRank.EveryWordPrefix;
  return MatchRank.NoMatch;
};

/**
 * Filters and ranks launcher actions for a query.
 *
 * An empty query returns the list in registration order, so the launcher opens
 * on the registry's own order (Actions, then Navigate, then Preferences) rather
 * than on whatever an empty match would happen to produce. `searchOnly` actions
 * (the "Go to" sub-pages) are left out of it: there are hundreds, and the
 * default list must stay the short one people already know. Ties keep
 * registration order — `Array.prototype.sort` is stable in every engine we
 * target.
 */
export default function filterActions(
  actions: QuickLauncherAction[],
  rawQuery: string
): QuickLauncherAction[] {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return actions.filter((action) => !action.searchOnly);

  return actions
    .map((action) => ({ action, rank: rankAction(action, query) }))
    .filter(({ rank }) => rank !== MatchRank.NoMatch)
    .sort((a, b) => a.rank - b.rank)
    .map(({ action }) => action);
}
